import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";

type CourseDetailType = {
  id: string;
  title: string;
  slug: string;
  description: string;
  thumbnail?: string;
  price?: number;
  hasAccess: boolean;
  accessExpiresAt: string | null;
  accessSource: string | null;
  lessons: { id: string; title: string; orderIndex: number }[];
};

export function CourseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState<CourseDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessCode, setAccessCode] = useState("");
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemMessage, setRedeemMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/courses/${slug}`)
      .then((res) => {
        if (res.data.success) setCourse(res.data.data);
      })
      .catch(() => setError("Course not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  const handlePurchaseCourse = async () => {
    if (!user || !course) return;
    setCheckoutLoading(true);
    setRedeemMessage(null);
    try {
      const { data } = await api.post("/checkout", { courseId: course.id });
      if (data.success && data.data?.url) {
        window.location.assign(data.data.url as string);
        return;
      }
      setRedeemMessage({
        type: "error",
        text: data.message || "Could not start checkout.",
      });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setRedeemMessage({ type: "error", text: msg || "Could not start checkout." });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessCode.trim() || !user) return;
    setRedeemLoading(true);
    setRedeemMessage(null);
    try {
      const { data: redeemRes } = await api.post("/enroll/access-code", { code: accessCode.trim() });
      if (redeemRes.success) {
        // Don't optimistically flip access to true for every success response.
        // The backend may enroll the user but only grant access to specific courseIds.
        const currentCourseId = course?.id;
        const courseIds: string[] = Array.isArray(redeemRes.courseIds) ? redeemRes.courseIds : [];
        const hasAccessForThisCourse = currentCourseId ? courseIds.includes(currentCourseId) : false;

        setRedeemMessage({
          type: "success",
          text: hasAccessForThisCourse ? "Enrolled! Go to your dashboard to start." : "Code redeemed, but no access for this course.",
        });
        setCourse((prev) => (prev ? { ...prev, hasAccess: hasAccessForThisCourse } : null));

        // Re-fetch course to get the correct `hasAccess` and `accessExpiresAt`.
        // This prevents stale/incorrect UI if the local optimistic check is wrong.
        try {
          const refreshedRes = await api.get(`/courses/${slug}`);
          if (refreshedRes.data.success) setCourse(refreshedRes.data.data);
        } catch (_) {
          // If refresh fails, keep the optimistic state from `hasAccessForThisCourse`.
        }
      } else {
        setRedeemMessage({ type: "error", text: redeemRes.message || "Invalid code." });
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setRedeemMessage({ type: "error", text: msg || "Could not redeem code." });
    } finally {
      setRedeemLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-stone/40">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-4 w-32 bg-gray-200 rounded" />
            <div className="h-10 w-3/4 bg-gray-200 rounded" />
            <div className="h-24 bg-gray-100 rounded-xl" />
            <div className="h-12 w-40 bg-gray-200 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }
  if (error || !course) {
    return (
      <div className="min-h-full bg-stone/40 flex items-center justify-center px-4 py-16">
        <div className="rounded-2xl border border-red-200 bg-white p-10 text-center shadow-soft max-w-md">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center text-red-500">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-red-600 font-medium mt-4">{error || "Course not found."}</p>
          <Link to="/courses" className="mt-6 inline-block font-semibold text-gold-500 hover:text-gold-400 transition-colors">
            Back to catalog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-stone/40">
      <div className="bg-ink-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to catalog
          </Link>
          <p className="text-gold-300/90 font-semibold text-xs tracking-[0.2em] uppercase mt-6">
            Course
          </p>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight mt-2">
            {course.title}
          </h1>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" aria-hidden />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-2xl border border-gray-200 bg-white p-8 md:p-10 shadow-soft-lg">
          <h2 className="font-serif text-lg font-semibold text-ink-950 tracking-tight">
            About this course
          </h2>
          <p className="mt-4 text-gray-600 leading-relaxed">
            {course.description}
          </p>
          {course.price !== undefined && (
            <p className="mt-3 text-sm text-gray-500">
              Price: ${Number(course.price || 0).toFixed(2)}
            </p>
          )}

          {Array.isArray(course.lessons) && course.lessons.length > 0 && (
            <div className="mt-8 rounded-xl border border-gray-200 bg-stone/30 p-5">
              <h3 className="font-semibold text-ink-950 text-sm">Lessons in this course</h3>
              <ul className="mt-3 space-y-2">
                {course.lessons.map((lesson, index) => (
                  <li key={lesson.id} className="text-sm text-gray-700">
                    {index + 1}. {lesson.title}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {redeemMessage && (
            <div
              className={`mt-6 rounded-xl px-4 py-3.5 text-sm font-medium flex items-center gap-3 ${
                redeemMessage.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-600 border border-red-200"
              }`}
            >
              {redeemMessage.type === "success" ? (
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              {redeemMessage.text}
            </div>
          )}

          {course.hasAccess ? (
            <div className="mt-8 pt-8 border-t border-gray-100">
              <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 mb-6 flex items-center gap-3 text-green-700 text-sm font-medium">
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                You have access to this course.
              </div>
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center gap-2 rounded-lg bg-gold-400 px-6 py-3.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-md"
              >
                Go to dashboard
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
              {course.accessExpiresAt && (
                <p className="mt-3 text-sm text-gray-500">
                  Access expires {new Date(course.accessExpiresAt).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="mt-8 rounded-xl border border-gold-400/30 bg-gold-50/50 p-5">
                <h3 className="font-semibold text-ink-950 text-sm">Get access</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Pay once for this course (6 months of access), subscribe to a plan for all courses, or redeem an access code.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {user && Number(course.price) > 0 && (
                    <button
                      type="button"
                      disabled={checkoutLoading}
                      onClick={handlePurchaseCourse}
                      className="inline-flex items-center gap-2 rounded-lg bg-gold-400 px-6 py-3.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-md disabled:opacity-60"
                    >
                      {checkoutLoading ? "Redirecting…" : `Pay $${Number(course.price).toFixed(2)} — 6 months`}
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </button>
                  )}
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 rounded-lg border border-ink-800/20 bg-white px-6 py-3.5 text-ink-950 font-semibold hover:bg-stone/50 transition-colors shadow-sm"
                  >
                    View all-course plans
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                  {!user && (
                    <span className="text-sm text-gray-500">
                      <Link to="/login" className="font-semibold text-gold-500 hover:text-gold-400">Log in</Link>
                      {" or "}
                      <Link to="/signup" className="font-semibold text-gold-500 hover:text-gold-400">Sign up</Link>
                      {" to purchase."}
                    </span>
                  )}
                </div>
              </div>
              {user && (
                <div className="mt-6 rounded-xl border border-gray-200 bg-stone/30 p-5">
                  <h3 className="font-semibold text-ink-950 text-sm">Have an access code?</h3>
                  <form onSubmit={handleRedeem} className="mt-3 flex flex-wrap gap-3">
                    <input
                      type="text"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 min-w-[140px] rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                    />
                    <button
                      type="submit"
                      disabled={redeemLoading}
                      className="rounded-lg bg-ink-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800 disabled:opacity-50 transition-colors"
                    >
                      {redeemLoading ? "Redeeming…" : "Redeem"}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
